import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '蜜桃tv',
      url: 'https://shiyangfz.cn',
      description: aboutData.brand.description.replace(/\n/g, ' ').substring(0, 200),
      foundingDate: '2019',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京科技园区A座16层',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: aboutData.contact.phone,
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
      sameAs: ['https://weibo.com/mitaotv', 'https://space.bilibili.com/mitaotv'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '关于蜜桃tv - 品牌介绍与联系方式',
      description: '了解蜜桃tv的品牌故事、发展历程、用户口碑与联系方式。蜜桃tv是备受用户喜爱的视频内容平台。',
      url: 'https://shiyangfz.cn/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: aboutData.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="关于蜜桃tv - 品牌介绍 | 发展历程 | 联系方式 | 常见问题"
        description="了解蜜桃tv的品牌故事与发展历程。蜜桃tv成立于2019年，累计服务超5000万用户，提供海量精选影视、综艺、短视频资源。查看用户口碑与联系方式。"
        keywords="蜜桃tv,蜜桃tv官网,关于蜜桃tv,蜜桃tv联系方式,蜜桃tv品牌介绍,蜜桃tv常见问题"
        canonicalUrl="https://shiyangfz.cn/about"
        schemaData={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[1100px] mx-auto pt-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-100/60 via-background-50 to-accent-100/40"></div>
            <div className="relative z-10 max-w-[1100px] mx-auto">
              <div className="text-center max-w-2xl mx-auto mb-12">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  关于蜜桃tv
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                  {aboutData.brand.slogan}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                  { value: '5000万+', label: '累计用户' },
                  { value: '800万+', label: '日活用户' },
                  { value: '10万+', label: '精选内容' },
                  { value: '100亿+', label: '总播放量' },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 rounded-xl bg-background-50/80 border border-background-200/50"
                  >
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-12">
            <div className="max-w-[1100px] mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                    品牌故事
                  </h2>
                  <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4 whitespace-pre-line">
                    {aboutData.brand.description}
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20warm%20lighting%2C%20designers%20working%20on%20large%20monitors%20displaying%20video%20content%2C%20collaborative%20atmosphere%2C%20peach%20and%20coral%20accent%20walls%2C%20indoor%20plants%2C%20minimalist%20Scandinavian%20design%2C%20professional%20yet%20cozy%20environment&width=800&height=500&seq=mitao-about-brand&orientation=landscape"
                    alt="蜜桃tv办公环境"
                    className="w-full h-full object-cover"
                    title="蜜桃tv - 办公环境"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Brand Values */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1100px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutData.brand.values.map((value) => (
                  <div
                    key={value.title}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                  >
                    <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className={`${value.icon} text-xl text-primary-600`}></i>
                    </span>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="w-full px-4 md:px-6 py-12">
            <div className="max-w-[1100px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8 text-center">
                发展历程
              </h2>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden lg:block"></div>
                <div className="space-y-8">
                  {aboutData.milestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`flex flex-col lg:flex-row items-start gap-6 ${
                        index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      <div className="flex-1 lg:text-right">
                        <div
                          className={`p-5 rounded-xl bg-background-50 border border-background-200/70 ${
                            index % 2 === 0 ? 'lg:mr-6' : 'lg:ml-6'
                          }`}
                        >
                          <div className="inline-flex items-center gap-2 mb-2">
                            <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
                              <span className="text-background-50 text-xs font-bold">
                                {milestone.year.slice(2)}
                              </span>
                            </span>
                            <h3 className="font-semibold text-foreground-950">{milestone.title}</h3>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {milestone.description}
                          </p>
                        </div>
                      </div>
                      {/* Timeline dot */}
                      <div className="hidden lg:flex items-center justify-center flex-shrink-0">
                        <div className="w-4 h-4 rounded-full bg-primary-500 border-4 border-background-100"></div>
                      </div>
                      <div className="flex-1 hidden lg:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1100px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutData.testimonials.map((item, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-primary-600 text-sm font-semibold">
                          {item.author.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">
                          {item.author}
                        </div>
                        <div className="text-xs text-foreground-500">{item.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-12" id="contact">
            <div className="max-w-[1100px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-4">
                  {[
                    {
                      icon: 'ri-map-pin-line',
                      label: '公司地址',
                      value: aboutData.contact.address,
                    },
                    {
                      icon: 'ri-phone-line',
                      label: '客服电话',
                      value: aboutData.contact.phone,
                    },
                    {
                      icon: 'ri-mail-line',
                      label: '电子邮箱',
                      value: aboutData.contact.email,
                    },
                    {
                      icon: 'ri-wechat-line',
                      label: '官方微信',
                      value: aboutData.contact.wechat,
                    },
                    {
                      icon: 'ri-time-line',
                      label: '服务时间',
                      value: aboutData.contact.businessHours,
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70"
                    >
                      <span className="w-10 h-10 rounded-lg bg-primary-100 flex-shrink-0 flex items-center justify-center">
                        <i className={`${item.icon} text-lg text-primary-600`}></i>
                      </span>
                      <div>
                        <div className="text-xs text-foreground-500">{item.label}</div>
                        <div className="text-sm font-medium text-foreground-950">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Map placeholder */}
                <div className="rounded-xl overflow-hidden border border-background-200/70 h-full min-h-[320px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12224.142135087326!2d116.480456!3d40.002567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f1a9a5a5a5a5a5%3A0x5a5a5a5a5a5a5a5a!2z5YyX5Lqs5biC5pyd6Ziz5Yy65pyb5Yqg56eR5oqA5Zut!5e0!3m2!1szh-CN!2scn!4v1620000000000!5m2!1szh-CN!2scn"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '320px' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="蜜桃tv公司地址"
                    aria-label="蜜桃tv公司地址地图"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[800px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-8 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {aboutData.faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                    >
                      <h4 className="text-sm font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </h4>
                      <span
                        className={`w-6 h-6 flex-shrink-0 flex items-center justify-center transition-transform duration-200 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </span>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}